/**
 * The pivot table.
 *
 * Nested column headers, row headers, and grand totals that refuse to print a
 * number they cannot justify. NEITHER RULE LIVES HERE. What the grid contains —
 * which rows print, where the subtotals land, which totals mean anything — is
 * `pivotLayout()` in semantic-core, because the Excel export walks the same
 * layout to place its formulas and a second walk would drift from this one.
 * This component turns that layout into a table: formatting, classes, sort
 * affordances.
 *
 * Every axis member arrives as a key AND a label. The label is what is shown;
 * the key is what identifies the row — including as its React key, so two
 * people with the same name stay two rows.
 */
import type {
  DimTuple,
  PivotColumn,
  PivotRow,
  PivotView,
  ReportModel,
  ReportSpec,
} from '@clockwise/semantic-core';
import {
  cellCoordinate,
  findDimension,
  findMeasure,
  isLevelSort,
  pivotLayout,
  showAsFor,
  tupleKey,
} from '@clockwise/semantic-core';
import { DefinitionHoverCard } from '@clockwise/ui';
import { SquareMinus, SquarePlus } from 'lucide-react';
import type { ReactElement } from 'react';
import { EMPTY_CELL, resolvedFormatter } from '../format';
import { coordinateLabels, type OnDrill } from './drill';

interface Props {
  model: ReportModel;
  spec: ReportSpec;
  view: PivotView;
  /**
   * Called when a header is clicked. OMIT and headers render as plain text —
   * a placed dashboard tile must not drift from the order it was saved with,
   * and a header that looks clickable but is not is worse than one that does
   * not.
   */
  onSort?: (axis: 'rows' | 'cols', by: string, at?: readonly string[], on?: string) => void;
  /**
   * Fold or unfold one group, by `groupKey()`. OMIT and no fold control is
   * drawn — the spec's folds still apply, exactly as a tile keeps its sort.
   */
  onToggleGroup?: (key: string) => void;
  /** Fold or unfold every group at one row level. Drawn only beside `onToggleGroup`. */
  onCollapseLevel?: (dimensionId: string, collapsed: boolean) => void;
  /**
   * Measure id → its formula as the SERVER states it (`meta.formulas`). Wins
   * over the model's `formulaText`, as in the footer's ƒ definitions, so a
   * formula the server changed shows on the header without a client deploy —
   * and the header and the footer never disagree on one screen.
   */
  formulas?: Readonly<Record<string, string>>;
  /**
   * A figure was clicked, to drill into. OMIT and every cell is plain text —
   * the same seam as the others: a placed tile has nowhere to send a click.
   *
   * Offered on every cell that prints a number: a value, a subtotal, a folded
   * group, a total. Not on an em dash — a total that is not a quantity, or a
   * cell with nothing in it, has no rows behind it to show.
   */
  onDrill?: OnDrill;
  /**
   * `ResultMeta.truncated` — the SERVER's cap. Renames the bottom row
   * "Total (partial)" and marks it, because that is the figure a reader
   * carries away.
   *
   * OR-ed below with `view.rowsDropped`, the projection's own cap: the two are
   * different truncations and either one makes the bottom row partial.
   */
  truncated?: boolean;
}

export function PivotTable({
  model,
  spec,
  view,
  onSort,
  onToggleGroup,
  onCollapseLevel,
  truncated,
  formulas,
  onDrill,
}: Props) {
  // EITHER cap. `meta.truncated` is the query stopping at its ceiling;
  // `view.rowsDropped` is the projection stopping at `spec.limit` after the
  // ranking, which the server cannot see and so never reports.
  const partial = truncated === true || view.rowsDropped;
  const layout = pivotLayout(model, spec, view, { truncated: partial });
  // Folded rows stay in the LAYOUT — the spreadsheet writes them as hidden
  // outline rows — and leave only the screen.
  const visibleRows = layout.rows.filter((row) => !row.hidden);
  /**
   * What each level's header control would do: unfold when every group it can
   * see is folded, fold otherwise. `null` where no group at that level is on
   * screen — the innermost level, or every group of it folded inside a parent —
   * because a control whose effect cannot be seen reads as broken.
   */
  const levelState = layout.rowDimensions.map((_id, i) => {
    let seen = false;
    for (const row of visibleRows) {
      const toggle = row.head[i]?.toggle;
      if (!toggle) continue;
      if (!toggle.collapsed) return 'open' as const;
      seen = true;
    }
    return seen ? ('folded' as const) : null;
  });
  /** Every level but the innermost can fold, so only those hold the icon's space. */
  const foldable = (i: number) => i < layout.rowDimensions.length - 1;
  const levelToggle = (rowDim: string, i: number) =>
    onToggleGroup && onCollapseLevel && foldable(i) ? (
      <LevelToggle
        state={levelState[i] ?? null}
        name={label(rowDim)}
        onToggle={(collapse) => onCollapseLevel(rowDim, collapse)}
      />
    ) : null;
  const colDims = spec.cols;
  const measures = spec.measures;
  const nMeas = measures.length;

  const label = (id: string) => findDimension(model, id)?.label ?? id;
  /** A drilled figure's show-as mode, when it is printed as a share. */
  const shownAs = (measureId: string) => {
    const mode = showAsFor(spec, measureId);
    return mode === 'raw' ? {} : { showAs: mode };
  };
  /**
   * One lookup per MEASURE per render. The label is read by every body cell
   * and every copy of a nested header, and each header wants the description
   * and formula too — resolving the model each time was three linear scans
   * per header copy. A plain map, not a hook: this component has none.
   */
  const measureDefs = new Map<string, { label: string; description?: string; formula?: string }>();
  const measureDef = (id: string) => {
    let def = measureDefs.get(id);
    if (!def) {
      const measure = findMeasure(model, id);
      def = {
        label: measure?.label ?? id,
        description: measure?.description,
        // The formula belongs on the column it produced: "where did this number
        // come from?" is asked while looking at the number, not in a footer.
        // The server's wording first — see `formulas`.
        formula: formulas?.[id] ?? measure?.formulaText,
      };
      measureDefs.set(id, def);
    }
    return def;
  };
  const measureLabel = (id: string) => measureDef(id).label;
  const measureFormula = (id: string) => measureDef(id).formula;
  /**
   * A measure header's label, with its definition ON DEMAND — pointing at the
   * label (or focusing its sort button) shows the description with the
   * formula under it. Nothing is drawn on the header for it: a glyph on every
   * column was a row of marks nobody asked for.
   */
  const measureHead = (id: string, label: ReactElement | string) => {
    const { description, formula } = measureDef(id);
    return (
      <DefinitionHoverCard description={description} formula={formula}>
        <span className="rk-headtip">{label}</span>
      </DefinitionHoverCard>
    );
  };

  /**
   * One resolution per MEASURE, reused across every cell it renders — a pivot
   * draws the same measure in every column band, so resolving per cell would
   * walk the model once per number in the grid.
   */
  const resolvedCells = new Map<string, ReturnType<typeof resolvedFormatter>>();
  const cells = (measureId: string) => {
    let made = resolvedCells.get(measureId);
    if (!made) {
      made = resolvedFormatter(model, spec, measureId);
      resolvedCells.set(measureId, made);
    }
    return made;
  };
  const cell = (value: number | null, measureId: string) => cells(measureId).format(value);
  /** The negative class for this cell, or `undefined`. */
  const cellTone = (value: number | null, measureId: string) => cells(measureId).className(value);

  /**
   * The group border sits at the start of each band — but only where there ARE
   * bands. With no column axis the value columns are one undivided run, and a
   * border before the first of them would draw a line down the table's left
   * edge; the trailing total band is always its own band and always gets one.
   */
  const groupStart = (column: PivotColumn) =>
    (column.isTotalBand || colDims.length > 0) && column.bandStart;

  /**
   * A ROW-HEADER COLUMN THAT MUST KEEP ITS VALUE ON ONE LINE.
   *
   * The same rule the listing applies, for the same reason: `2026-08-18` has a
   * break opportunity after each hyphen, so under `table-layout: auto` a wide
   * neighbour — here a customer name in the row header beside it — can squeeze
   * the date column below the width of a date and split it in two. `isTime` is
   * the model's own word for the columns this can happen to.
   *
   * Indexed by POSITION, not looked up per cell: `head[i]` is one entry per row
   * dimension left to right, so it lines up with `rowDimensions` on every row
   * kind.
   *
   * VALUE ROWS ONLY, though. A subtotal writes its caption into the column of
   * the level it closes, so on a time level that cell holds "2026-07-01 total"
   * — and pinned, THAT string becomes the column's minimum instead of the date,
   * widening it by half again for every row above it. The caption is one cell
   * in one row and can afford to wrap; the dates it sits under cannot.
   */
  const rowDimNowrap = layout.rowDimensions.map((id) => findDimension(model, id)?.isTime === true);

  return (
    // `rk-pivot-scroll` is what makes the header pinnable, and it is the
    // listing's `rk-detail-scroll` under another name: `overflow-x: auto`
    // already makes this box a scroll container, but at CONTENT height its
    // scrollport is the whole table — a container that never scrolls — so a
    // sticky `thead` inside it has nowhere to travel and silently does
    // nothing. The cap gives it a height to scroll WITHIN. A host that bounds
    // the grid itself says `--rk-table-height: none` and the cap stands down;
    // the web workbench does exactly that above 901px, where the preview
    // column is the real edge.
    <div className="rk-table-scroll rk-pivot-scroll">
      <table className="rk-table rk-pivot">
        <thead>
          {colDims.length > 0 ? (
            <>
              {layout.headerLevels.map((level, levelIndex) => (
                <tr key={level.dimensionId}>
                  {levelIndex === 0 &&
                    spec.rows.map((rowDim, i) => (
                      <th
                        key={rowDim}
                        className="rk-rowdim"
                        rowSpan={colDims.length + 1}
                        scope="col"
                        aria-sort={ariaSort(spec.rowSort, rowDim, 'label')}
                      >
                        {/* Each level, not just the first. A `label` order is
                            a property of ONE level now, so the header that
                            sets it is that level's own — which is also what
                            makes the marker land where the reader is looking. */}
                        <span className="rk-foldhead">
                          {levelToggle(rowDim, i)}
                          <SortHeader
                            label={label(rowDim)}
                            onSort={
                              onSort ? () => onSort('rows', 'label', undefined, rowDim) : undefined
                            }
                            marker={sortMarker(spec.rowSort, rowDim, 'label')}
                          />
                        </span>
                      </th>
                    ))}
                  {level.cells.map((headerCell) => (
                    <th
                      key={`${headerCell.path}#${headerCell.start}`}
                      className="rk-colgroup"
                      colSpan={headerCell.colSpan}
                      scope="colgroup"
                    >
                      {headerCell.member}
                    </th>
                  ))}
                  {levelIndex === 0 && layout.showColTotal && (
                    <th
                      className="rk-colgroup rk-total"
                      colSpan={nMeas}
                      rowSpan={colDims.length}
                      scope="colgroup"
                    >
                      Total
                    </th>
                  )}
                </tr>
              ))}
              <tr>
                {layout.columns.map((column) => (
                  <th
                    key={columnKey(column)}
                    className={`rk-measure${groupStart(column) ? ' rk-groupstart' : ''}`}
                    scope="col"
                    // The total band totals what the sortable headers sort; it
                    // is not itself an order the table can be put in.
                    aria-sort={
                      column.isTotalBand
                        ? undefined
                        : ariaSort(spec.rowSort, spec.rows[0], column.measureId, column.colKey)
                    }
                  >
                    {/* Nested, this header repeats once per column group, so
                        the formula is on demand rather than printed four times
                        across the table. The TOTAL band too: same measure. */}
                    {measureHead(
                      column.measureId,
                      column.isTotalBand ? (
                        measureLabel(column.measureId)
                      ) : (
                        <SortHeader
                          label={measureLabel(column.measureId)}
                          // Sorts rows by THIS column, not by the measure summed
                          // across every column — four headers producing one
                          // identical order reads as broken.
                          onSort={
                            onSort
                              ? () =>
                                  onSort(
                                    'rows',
                                    column.measureId,
                                    column.colKey.map((v) => v.key ?? ''),
                                  )
                              : undefined
                          }
                          marker={sortMarker(
                            spec.rowSort,
                            spec.rows[0],
                            column.measureId,
                            column.colKey,
                          )}
                        />
                      ),
                    )}
                  </th>
                ))}
              </tr>
            </>
          ) : (
            <tr>
              {/* ONE CONTROL PER LEVEL. This used to be a single button on the
                  first column, because a `label` sort ordered EVERY level of
                  the axis at once and N buttons doing one thing would have
                  marked N columns as the one deciding the order. A level now
                  has an order of its own, so each header sets its own and
                  carries its own marker. */}
              {spec.rows.map((rowDim, i) => (
                <th key={rowDim} scope="col" aria-sort={ariaSort(spec.rowSort, rowDim, 'label')}>
                  <span className="rk-foldhead">
                    {levelToggle(rowDim, i)}
                    <SortHeader
                      label={label(rowDim)}
                      onSort={onSort ? () => onSort('rows', 'label', undefined, rowDim) : undefined}
                      marker={sortMarker(spec.rowSort, rowDim, 'label')}
                    />
                  </span>
                </th>
              ))}
              {measures.map((measureId) => (
                <th
                  key={measureId}
                  className="rk-measure"
                  scope="col"
                  aria-sort={ariaSort(spec.rowSort, spec.rows[0], measureId)}
                  // SEEN on demand, HEARD always. Printed inline it turned a
                  // one-line header into four under five measures — "Working
                  // days × 8 − (Holiday + PTO). For a part-time employee…"
                  // wrapped down the column — and pushed the numbers the report
                  // exists to show below the fold. So the eye gets the
                  // description and formula on demand, by pointing at the
                  // label, and the footer's ƒ definitions; a screen reader
                  // still announces "Units, sum of units sold", because a
                  // definition that travels with the column is the whole
                  // reason `formulaText` ships with every result.
                >
                  {measureHead(
                    measureId,
                    <SortHeader
                      label={measureLabel(measureId)}
                      onSort={onSort ? () => onSort('rows', measureId) : undefined}
                      marker={sortMarker(spec.rowSort, spec.rows[0], measureId)}
                    />,
                  )}
                  <span className="rk-sr">{measureFormula(measureId)}</span>
                </th>
              ))}
            </tr>
          )}
        </thead>

        <tbody>
          {visibleRows.map((row) => (
            <tr
              key={rowKey(row)}
              className={
                [
                  row.collapsed ? 'rk-collapsedrow' : ROW_CLASS[row.kind],
                  // The bottom row only, and only when it is short: a subtotal
                  // over a truncated result is partial too, but marking every
                  // band turns the warning into wallpaper. One row carries it.
                  partial && row.kind === 'total' ? 'rk-partialrow' : undefined,
                ]
                  .filter(Boolean)
                  .join(' ') || undefined
              }
            >
              {row.head.map((head, i) => (
                <th
                  key={`${layout.rowDimensions[i]}-${head.key ?? ''}`}
                  scope="row"
                  // Deliberately not on the `rk-rowdim` header above: one
                  // unwrapping body cell already floors the column at a whole
                  // date, so the label is free to wrap and cost nothing.
                  className={`rk-rowhead${head.repeat ? ' rk-rowhead-repeat' : ''}${
                    row.kind === 'value' && rowDimNowrap[i] ? ' rk-nowrap' : ''
                  }`}
                  data-key={head.key ?? undefined}
                >
                  {/* Control and label in ONE flex box, never two inline
                      boxes: a narrow row-head column breaks between them and
                      strands the ⊟ on a line above the name it folds. */}
                  {onToggleGroup && foldable(i) ? (
                    <span className="rk-foldhead">
                      {head.toggle ? (
                        <GroupToggle
                          collapsed={head.toggle.collapsed}
                          name={head.text || EMPTY_CELL}
                          onToggle={() => head.toggle && onToggleGroup(head.toggle.key)}
                        />
                      ) : (
                        <ToggleSlot />
                      )}
                      <span>{head.empty ? EMPTY_CELL : head.text}</span>
                    </span>
                  ) : head.empty ? (
                    EMPTY_CELL
                  ) : (
                    head.text
                  )}
                </th>
              ))}
              {row.cells.map((bodyCell, i) => {
                const column = layout.columns[i];
                if (!column) return null;
                return (
                  <td
                    key={columnKey(column)}
                    className={[
                      'rk-num',
                      bodyCell.position ? 'rk-total' : '',
                      groupStart(column) ? 'rk-groupstart' : '',
                      // Only a cell that actually prints a number can read as
                      // a negative one; an unmeaningful total prints an em dash.
                      bodyCell.meaningful
                        ? cellTone(bodyCell.value, bodyCell.measureId)
                        : undefined,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {/* A total renders only where it means something: a
                        non-additive measure's is not the sum of its parts, and
                        a percentage totalled across the axis it was NOT
                        computed over is not a quantity. */}
                    {onDrill && bodyCell.meaningful && bodyCell.value !== null ? (
                      <DrillButton
                        text={cell(bodyCell.value, bodyCell.measureId)}
                        name={[
                          measureLabel(bodyCell.measureId),
                          ...tupleLabels(row.kind === 'total' ? undefined : row.key),
                          ...tupleLabels(column.isTotalBand ? undefined : column.colKey),
                        ].join(', ')}
                        onClick={(el) =>
                          onDrill({
                            at: cellCoordinate(
                              spec,
                              row.kind === 'total' ? undefined : row.key,
                              column.isTotalBand ? undefined : column.colKey,
                            ),
                            labels: coordinateLabels(
                              spec,
                              row.kind === 'total' ? undefined : row.key,
                              column.isTotalBand ? undefined : column.colKey,
                            ),
                            measureId: bodyCell.measureId,
                            text: cell(bodyCell.value, bodyCell.measureId),
                            ...shownAs(bodyCell.measureId),
                            anchor: () => (el.isConnected ? el.getBoundingClientRect() : null),
                            returnFocus: el,
                          })
                        }
                      />
                    ) : bodyCell.meaningful ? (
                      cell(bodyCell.value, bodyCell.measureId)
                    ) : (
                      EMPTY_CELL
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Labels of a tuple, for a cell's accessible name. A missing value reads as such. */
const tupleLabels = (tuple: DimTuple | undefined) =>
  (tuple ?? []).map((v) => v.label ?? v.key ?? 'no value');

/**
 * A figure you can drill into. A real BUTTON, so Tab reaches it and Enter opens
 * the menu — a click handler on the cell would be a mouse-only feature. Named
 * by the measure and the coordinate, because "62.4%" alone does not say which
 * of a grid of them was meant.
 */
function DrillButton({
  text,
  name,
  onClick,
}: {
  text: string;
  name: string;
  onClick: (el: HTMLButtonElement) => void;
}) {
  return (
    <button
      type="button"
      className="rk-drillcell"
      aria-haspopup="menu"
      aria-label={`${name}: ${text}. Drill into this figure`}
      onClick={(event) => onClick(event.currentTarget)}
    >
      {text}
    </button>
  );
}

const ROW_CLASS: Record<PivotRow['kind'], string | undefined> = {
  value: undefined,
  subtotal: 'rk-subtotalrow',
  total: 'rk-totalrow',
};

/**
 * A column's React key.
 *
 * `T` and `C` keep the total band in its own namespace: without them a column
 * dimension whose key happened to be the string "total" would collide with it.
 */
function columnKey(column: PivotColumn): string {
  const band = column.isTotalBand ? 'T' : `C${tupleKey(column.colKey)}`;
  return `${band}-${column.measureId}`;
}

/**
 * A body row's React key.
 *
 * Each kind gets its own namespace, for the reason {@link columnKey} does: a
 * dimension declared with a label and no id uses its own TEXT as the key, so a
 * leaf row could otherwise be keyed the same as the row named "Total".
 */
function rowKey(row: PivotRow): string {
  if (row.kind === 'total') return 'T';
  const key = row.key ? tupleKey(row.key) : '';
  return row.kind === 'subtotal' ? `S${row.level}-${key}` : `R${key}`;
}

/**
 * The entry that decides ONE LEVEL of an axis, or nothing.
 *
 * A pivot's axis is nested and each level has an order of its own, so a header
 * is marked by the entry for the level it belongs to. A chain with no `on`
 * anywhere is the original whole-axis form — it decides every level, head
 * first, so its head is what marks them, which is what this did before levels
 * could be named. A chain that HAS levels marks only the ones it names: a
 * legacy tie-breaker sitting beside per-level entries decides nothing.
 */
function entryFor(chain: ReportSpec['rowSort'], level: string | undefined) {
  const own = chain.find((s) => s.on === level);
  if (own) return own;
  return chain.some((s) => s.on !== undefined) ? undefined : chain[0];
}

/**
 * Whether a sort spec is the one this header represents.
 *
 * A level's own header stands for the level's OWN order, by name or by key
 * alike — "Data source order" run backwards is still that level being ordered
 * by itself, and a newest-first month column with no arrow says nothing about
 * why it runs backwards.
 */
function isSortedBy(
  chain: ReportSpec['rowSort'],
  level: string | undefined,
  by: string,
  at?: DimTuple,
): boolean {
  const sort = entryFor(chain, level);
  if (!sort) return false;
  if (isLevelSort(by) ? !isLevelSort(sort.by) : sort.by !== by) return false;
  const want = at ? at.map((v) => v.key ?? '') : undefined;
  const have = sort.at;
  if (!want?.length) return !have?.length;
  return have?.length === want.length && have.every((k, i) => k === want[i]);
}

function sortMarker(
  chain: ReportSpec['rowSort'],
  level: string | undefined,
  by: string,
  at?: DimTuple,
): string {
  if (!isSortedBy(chain, level, by, at)) return '';
  return entryFor(chain, level)?.dir === 'asc' ? '\u2191' : '\u2193';
}

function ariaSort(
  chain: ReportSpec['rowSort'],
  level: string | undefined,
  by: string,
  at?: DimTuple,
): 'ascending' | 'descending' | 'none' {
  if (!isSortedBy(chain, level, by, at)) return 'none';
  return entryFor(chain, level)?.dir === 'asc' ? 'ascending' : 'descending';
}

/**
 * The ⊟/⊞ in front of a group's first row.
 *
 * `aria-expanded` rather than `aria-pressed`: the control does not have a
 * state of its own, it reports the state of the rows beneath it — which is what
 * a screen reader announces as "collapsed" or "expanded".
 */
function GroupToggle({
  collapsed,
  name,
  onToggle,
}: {
  collapsed: boolean;
  name: string;
  onToggle: () => void;
}) {
  const Icon = collapsed ? SquarePlus : SquareMinus;
  return (
    <button
      type="button"
      className="rk-grouptoggle"
      aria-expanded={!collapsed}
      aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${name}`}
      title={collapsed ? 'Expand' : 'Collapse'}
      onClick={onToggle}
    >
      <Icon size={13} aria-hidden="true" />
    </button>
  );
}

/** The icon's footprint with no control in it, so labels do not shift. */
function ToggleSlot() {
  return (
    <span className="rk-grouptoggle rk-grouptoggle-slot" aria-hidden="true">
      <SquareMinus size={13} />
    </span>
  );
}

/** Fold or unfold every group at one level, from that level's header. */
function LevelToggle({
  state,
  name,
  onToggle,
}: {
  state: 'open' | 'folded' | null;
  name: string;
  onToggle: (collapse: boolean) => void;
}) {
  if (state === null) return <ToggleSlot />;
  const folded = state === 'folded';
  const Icon = folded ? SquarePlus : SquareMinus;
  const text = `${folded ? 'Expand' : 'Collapse'} every ${name}`;
  return (
    <button
      type="button"
      className="rk-grouptoggle"
      aria-label={text}
      title={text}
      onClick={() => onToggle(!folded)}
    >
      <Icon size={13} aria-hidden="true" />
    </button>
  );
}

/** A header that sorts when it can, and is plain text when it cannot. */
function SortHeader({
  label,
  marker,
  onSort,
}: {
  label: string;
  marker: string;
  onSort?: () => void;
}) {
  if (!onSort) return <>{label}</>;
  return (
    <button type="button" className="rk-sortable" onClick={onSort}>
      {label}
      {marker && (
        <span className="rk-sortmark" aria-hidden="true">
          {marker}
        </span>
      )}
    </button>
  );
}
